import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Badge, Alert, Spinner, ListGroup, Table, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { fetchCaseEntities, fetchEntityMentions, triggerExtractCaseEntities } from '../../api/cases';
import { useAuth } from '../../contexts/AuthContext';
import { CaseEntity, EntityMention, EntityType } from '../../types';
import { formatCurrency } from '../../utils/formatters';

interface Props {
  case_id: string;
}

const ENTITY_TYPE_LABELS: Record<EntityType | string, string> = {
  person:       'Person',
  business:     'Business',
  bank_account: 'Bank Account',
  gl_account:   'GL Account',
  vendor:       'Vendor',
  other:        'Other',
};

const ENTITY_TYPE_COLORS: Record<EntityType | string, string> = {
  person:       'primary',
  business:     'success',
  bank_account: 'warning',
  gl_account:   'info',
  vendor:       'secondary',
  other:        'dark',
};

const ALL_TYPES = Object.keys(ENTITY_TYPE_LABELS) as EntityType[];

const CaseEntities: React.FC<Props> = ({ case_id }) => {
  const auth = useAuth();
  const token = auth.user?.id_token;

  const [entities, setEntities]               = useState<CaseEntity[]>([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState<string | null>(null);
  const [typeFilter, setTypeFilter]           = useState<EntityType | 'all'>('all');
  const [selectedEntity, setSelectedEntity]   = useState<CaseEntity | null>(null);
  const [mentions, setMentions]               = useState<EntityMention[]>([]);
  const [mentionsLoading, setMentionsLoading] = useState(false);
  const [triggering, setTriggering]           = useState(false);
  const [triggerMessage, setTriggerMessage]   = useState<string | null>(null);

  const loadEntities = useCallback(() => {
    setLoading(true);
    fetchCaseEntities(case_id, token)
      .then(({ entities }) => setEntities(entities))
      .catch(e => setError(e?.message ?? 'Failed to load entities'))
      .finally(() => setLoading(false));
  }, [case_id, token]);

  useEffect(() => {
    loadEntities();
  }, [loadEntities]);

  const handleExtractEntities = async (fullRescan: boolean) => {
    setTriggering(true);
    setTriggerMessage(null);
    try {
      await triggerExtractCaseEntities(case_id, fullRescan, token);
      setTriggerMessage(
        fullRescan
          ? 'Full re-scan started — this reprocesses every file and can take several minutes for large cases. Refresh this tab shortly to see results.'
          : 'Started — processing any files not yet covered. Refresh this tab shortly to see new/updated entities.'
      );
    } catch (e: any) {
      setTriggerMessage(e?.message ?? 'Failed to start entity extraction.');
    } finally {
      setTriggering(false);
    }
  };

  const selectEntity = useCallback((entity: CaseEntity) => {
    if (selectedEntity?.entity_id === entity.entity_id) {
      setSelectedEntity(null);
      setMentions([]);
      return;
    }
    setSelectedEntity(entity);
    setMentionsLoading(true);
    fetchEntityMentions(case_id, entity.entity_id, token)
      .then(({ mentions }) => setMentions(mentions))
      .catch(() => setMentions([]))
      .finally(() => setMentionsLoading(false));
  }, [selectedEntity, case_id, token]);

  const filtered = typeFilter === 'all'
    ? entities
    : entities.filter(e => e.entity_type === typeFilter);

  const extractControls = (
    <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
      <Button
        variant="outline-secondary"
        size="sm"
        disabled={triggering}
        onClick={() => handleExtractEntities(false)}
      >
        {triggering ? <Spinner animation="border" size="sm" /> : 'Extract New Entities'}
      </Button>
      <Button
        variant="outline-secondary"
        size="sm"
        disabled={triggering}
        onClick={() => handleExtractEntities(true)}
        title="Reprocess every file in this case from scratch, not just files added since the last run"
      >
        Full Re-scan
      </Button>
      {triggerMessage && <span className="text-muted small">{triggerMessage}</span>}
    </div>
  );

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 mt-3">
        <Spinner animation="border" size="sm" />
        <span>Loading entities…</span>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger" className="mt-3">{error}</Alert>;
  }

  if (entities.length === 0) {
    return (
      <div className="mt-3">
        {extractControls}
        <Alert variant="info">
          <p className="mb-1"><strong>No entities extracted yet.</strong></p>
          <p className="mb-0 small">
            Entities are automatically extracted when files are processed. Upload and process a
            bank statement, general ledger, or check image file to populate this section.
          </p>
        </Alert>
      </div>
    );
  }

  return (
    <div className="mt-3">
      {extractControls}
      {/* Type filter chips */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        <Badge
          bg={typeFilter === 'all' ? 'dark' : 'light'}
          text={typeFilter === 'all' ? undefined : 'dark'}
          style={{ cursor: 'pointer', fontSize: '0.8rem', padding: '6px 10px' }}
          onClick={() => setTypeFilter('all')}
        >
          All ({entities.length})
        </Badge>
        {ALL_TYPES.map(t => {
          const count = entities.filter(e => e.entity_type === t).length;
          if (count === 0) return null;
          return (
            <Badge
              key={t}
              bg={typeFilter === t ? ENTITY_TYPE_COLORS[t] : 'light'}
              text={typeFilter === t ? undefined : 'dark'}
              style={{ cursor: 'pointer', fontSize: '0.8rem', padding: '6px 10px' }}
              onClick={() => setTypeFilter(typeFilter === t ? 'all' : t)}
            >
              {ENTITY_TYPE_LABELS[t]} ({count})
            </Badge>
          );
        })}
      </div>

      <Row>
        {/* Entity list (left column) */}
        <Col md={selectedEntity ? 4 : 12}>
          <ListGroup>
            {filtered.map(entity => {
              const isSelected = selectedEntity?.entity_id === entity.entity_id;
              return (
                <ListGroup.Item
                  key={entity.entity_id}
                  action
                  active={isSelected}
                  onClick={() => selectEntity(entity)}
                  className="d-flex justify-content-between align-items-start"
                  style={{ cursor: 'pointer' }}
                >
                  <div className="me-2">
                    <div className="fw-semibold">{entity.name}</div>
                    {entity.aliases && entity.aliases.length > 0 && (
                      <div className="small text-muted">
                        a.k.a. {entity.aliases.slice(0, 3).join(', ')}
                        {entity.aliases.length > 3 && ` +${entity.aliases.length - 3}`}
                      </div>
                    )}
                    {entity.attributes && Object.keys(entity.attributes).length > 0 && (
                      <div className="small text-muted">
                        {Object.entries(entity.attributes).slice(0, 2).map(([k, v]) => (
                          <span key={k} className="me-2">{k}: {v}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
                    <Badge
                      bg={ENTITY_TYPE_COLORS[entity.entity_type] ?? 'secondary'}
                      style={{ fontSize: '0.7rem' }}
                    >
                      {ENTITY_TYPE_LABELS[entity.entity_type] ?? entity.entity_type}
                    </Badge>
                    {entity.mention_count != null && entity.mention_count > 0 && (
                      <Badge bg="light" text="dark" style={{ fontSize: '0.7rem' }}>
                        {entity.mention_count} mention{entity.mention_count !== 1 ? 's' : ''}
                      </Badge>
                    )}
                    {entity.manually_reviewed && (
                      <Badge bg="success" style={{ fontSize: '0.65rem' }}>verified</Badge>
                    )}
                  </div>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </Col>

        {/* Mention detail (right column) */}
        {selectedEntity && (
          <Col md={8}>
            <Card className="h-100">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{selectedEntity.name}</strong>
                  <Badge
                    bg={ENTITY_TYPE_COLORS[selectedEntity.entity_type] ?? 'secondary'}
                    className="ms-2"
                    style={{ fontSize: '0.75rem' }}
                  >
                    {ENTITY_TYPE_LABELS[selectedEntity.entity_type] ?? selectedEntity.entity_type}
                  </Badge>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => { setSelectedEntity(null); setMentions([]); }}
                  aria-label="Close"
                />
              </Card.Header>
              <Card.Body className="p-0">
                {mentionsLoading ? (
                  <div className="d-flex align-items-center gap-2 p-3">
                    <Spinner animation="border" size="sm" />
                    <span>Loading mentions…</span>
                  </div>
                ) : mentions.length === 0 ? (
                  <p className="text-muted p-3 mb-0">No mentions found for this entity.</p>
                ) : (
                  <div style={{ maxHeight: '460px', overflowY: 'auto' }}>
                    <Table size="sm" hover className="mb-0">
                      <thead className="sticky-top bg-white table-light">
                        <tr>
                          <th>Date</th>
                          <th>Record Type</th>
                          <th>Description</th>
                          <th>Role</th>
                          <th className="text-end">Amount</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {mentions.map(m => (
                          <tr key={m.mention_id}>
                            <td className="text-nowrap">{m.mention_date ?? '—'}</td>
                            <td>
                              <span className="badge bg-light text-dark">{m.record_type}</span>
                            </td>
                            <td className="small">{m.description ?? '—'}</td>
                            <td className="small text-muted">{m.role ?? '—'}</td>
                            <td className="text-end small">
                              {m.amount != null ? formatCurrency(m.amount) : '—'}
                            </td>
                            <td className="text-nowrap">
                              {m.page_numbers && m.page_numbers.length > 0 ? (
                                <Link
                                  to={`/case/${case_id}/file/${m.file_id}?page=${m.page_numbers[0]}`}
                                  className="text-decoration-none"
                                  title={`Open file at page ${m.page_numbers[0]}`}
                                >
                                  <span
                                    className="badge"
                                    style={{
                                      background: '#e8f0fe',
                                      color: '#1a56db',
                                      fontSize: '0.72rem',
                                      fontWeight: 500,
                                    }}
                                  >
                                    <i className="bi bi-file-earmark-text me-1" />
                                    {m.page_numbers.length === 1
                                      ? `p. ${m.page_numbers[0]}`
                                      : `pp. ${m.page_numbers[0]}–${m.page_numbers[m.page_numbers.length - 1]}`}
                                  </span>
                                </Link>
                              ) : m.file_id ? (
                                <Link
                                  to={`/case/${case_id}/file/${m.file_id}`}
                                  className="small text-decoration-none text-muted"
                                  title="View source file"
                                >
                                  <i className="bi bi-box-arrow-up-right" />
                                </Link>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default CaseEntities;
